/**
 * Google Maps, for every surface on this platform that draws one.
 *
 * Lifted out of `apps/dispatch-console` when the rider and driver apps needed the same thing.
 * Nothing here knows which application it is in: the key is an argument rather than a read of
 * `import.meta.env`, because each application names its own variable and a package that reached
 * for one of them would only work in one of them.
 *
 * The three geometry types below are stated here rather than imported from a console, and they
 * are structurally identical to that console's own — which is what lets `projectionFromMap`
 * keep satisfying the interface `LiveMap` already takes, with no change at the call site.
 */
/**
 * Whether a given key means tiles. The rule, separated from where the key comes from.
 *
 * Split out because the test for it was asserting the *ambient environment* — "tiles are off"
 * — which passed on a machine with no key and failed on one that had configured it. A test whose
 * result depends on the developer's `.env` is a test that tells you about the developer.
 */
export function tilesAreConfiguredWith(key) {
    return key !== undefined && key.length > 0;
}
/**
 * One load per key, so two components mounting in the same tick await one script.
 *
 * Keyed rather than a single promise because this is a package now: one key per application is
 * the only case there is, but a map that silently came back loaded under somebody else's key
 * would be a bad way to find out otherwise.
 */
const loading = new Map();
/**
 * The global Google calls when the API is ready. A name, because the URL has to carry one.
 *
 * Namespaced rather than something like `initMap`: it lives on `window` for the few hundred
 * milliseconds between the request and the callback, and a console that later embeds anything
 * else should not be able to collide with it. Deleted as soon as it has fired.
 */
export const READY_CALLBACK = "__tdelivrGoogleMapsReady";
/**
 * Load the Maps JavaScript API once, however many maps ask for it.
 *
 * The script tag is the documented way in and it installs a global; loading it twice logs a
 * warning and re-registers everything. The promise is cached rather than the element, so two
 * components mounting in the same tick await one load.
 *
 * `loading=async` because Google now requires it for the newer libraries and warns without it,
 * and `v=weekly` so a pinned version does not quietly go out of support.
 *
 * ## `loading=async` means the script's `load` event is not the end of loading
 *
 * **This resolved too early and the live map drew the schematic surface on the deployed console
 * for it.** Under `loading=async` the script installs a *bootstrap*: `google.maps` exists, and
 * the only thing on it is `importLibrary`. Every class — `Map`, `LatLng`, `LatLngBounds` — is
 * attached later, by the import. So the old `load` handler saw `google.maps !== undefined`,
 * called that loaded, and the first caller ran `new google.maps.Map(…)` against an object that
 * had no `Map` on it yet.
 *
 * The failure was quiet in the worst way. The caller catches a dead map and falls back to the
 * schematic surface *by design* — that is the keyless path above, and it is a legitimate render —
 * so a console with a working, correctly restricted key drew exactly what a console with no key
 * draws. Measured on the deployed page: the loader and all three of its chunks returned 200,
 * `google.maps` was present, and the console logged
 * `Live map tiles unavailable; drawing the schematic surface TypeError: r.maps.Map is not a
 * constructor`. Nothing about the key, the referrer restriction or the billing account was
 * wrong, which is the whole reason this note is this long.
 *
 * Both libraries are imported because this file uses both: `maps` carries `Map`, and `core`
 * carries `LatLng` and `LatLngBounds`, which `boundsOf` constructs directly.
 */
export function loadGoogleMaps(key) {
    if (!tilesAreConfiguredWith(key) || key === undefined)
        return Promise.reject(new Error("No Google Maps API key is configured"));
    /*
     * `Map` rather than `maps`: the bootstrap satisfies `maps` on its own, so asking about `maps`
     * is asking the question that produced the bug.
     */
    const existing = globalThis.google;
    if (existing?.maps?.Map !== undefined)
        return Promise.resolve(existing);
    const inFlight = loading.get(key);
    if (inFlight !== undefined)
        return inFlight;
    const started = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        /*
         * `callback` is how `loading=async` says it is ready, and the script's own `load` event is
         * not.
         *
         * The second thing measured on the deployed console, after the bootstrap problem below: the
         * script element fired `load`, and `google.maps.importLibrary` was still undefined. The
         * request for `/maps/api/js` returns a small stub that fetches `main.js`, `common.js` and
         * `util.js` and only then installs anything — all three were served, with `load` firing in
         * the middle of that. So the element's `load` says the *stub* arrived, which is a different
         * event from the API being usable, and the only thing that says the second is this callback.
         * Google's documentation requires it whenever `loading=async` is set, and this is why.
         */
        globalThis[READY_CALLBACK] = () => {
            delete globalThis[READY_CALLBACK];
            const loaded = globalThis.google;
            if (loaded?.maps?.importLibrary === undefined) {
                reject(new Error("Google Maps loaded without installing its global"));
                return;
            }
            Promise.all([loaded.maps.importLibrary("maps"), loaded.maps.importLibrary("core")]).then(() => {
                if (loaded.maps.Map === undefined) {
                    reject(new Error("Google Maps imported its libraries without installing Map"));
                    return;
                }
                resolve(loaded);
            }, (cause) => {
                /* Same reasoning as the script's own error path below: let a later mount retry. */
                loading.delete(key);
                reject(new Error("Google Maps could not import its libraries", { cause }));
            });
        };
        script.src =
            `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}` +
                `&loading=async&v=weekly&callback=${READY_CALLBACK}`;
        script.async = true;
        script.addEventListener("error", () => {
            /*
             * Cleared so a later mount retries. A referrer-restricted key rejected once because the
             * console was opened on an unlisted hostname should not leave every subsequent map dead
             * for the life of the tab.
             */
            loading.delete(key);
            reject(new Error("Google Maps script failed to load"));
        });
        document.head.append(script);
    });
    loading.set(key, started);
    return started;
}
/**
 * A `Projection` backed by a real map, so every marker the live map already draws lands where the
 * tiles say it should.
 *
 * **Nothing about the marker layer changes.** It asks for percentages of the surface and gets
 * them; what differs is that the percentages now come from Google's view rather than from a fit
 * computed over the points. That is the whole integration, and it is why this is a function
 * returning the existing interface rather than a rewrite of `LiveMap`.
 *
 * Derived from the map's bounds rather than from `fromLatLngToContainerPixel`, and the reason is
 * worth keeping: the pixel method needs the overlay machinery mounted and returns coordinates
 * relative to a container whose size this code would then have to measure anyway. Bounds give the
 * same answer in two subtractions, and they are a plain data read that cannot be mid-animation.
 *
 * Returns `undefined` before the map has bounds — the first frame after construction — so the
 * caller keeps the fitted projection until there is a real one.
 */
export function projectionFromMap(map) {
    const bounds = map.getBounds();
    if (bounds === undefined)
        return undefined;
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    const north = northEast.lat();
    const south = southWest.lat();
    const east = northEast.lng();
    const west = southWest.lng();
    const latitudeSpan = north - south;
    /*
     * A view crossing the antimeridian has `east < west`, and the width is what remains of the
     * globe. No tenant operates across it, and a negative span would mirror every marker — cheap
     * to be right about.
     */
    const longitudeSpan = east >= west ? east - west : 360 - (west - east);
    if (latitudeSpan <= 0 || longitudeSpan <= 0)
        return undefined;
    const squeeze = Math.max(0.1, Math.cos((((north + south) / 2) * Math.PI) / 180));
    return {
        place(point) {
            const eastward = point.longitude >= west ? point.longitude - west : point.longitude + 360 - west;
            return {
                left: (eastward / longitudeSpan) * 100,
                // Latitude grows north and `top` grows south, so the axis is flipped rather than scaled.
                top: ((north - point.latitude) / latitudeSpan) * 100,
            };
        },
        /*
         * The accuracy circle's scale, in the same terms the fitted projection gives it: what share
         * of the surface's width one metre covers at this zoom. 111_320 metres per degree of
         * latitude, squeezed by the cosine for longitude, which is what the fitted projection does.
         */
        percentPerMetre: 100 / (longitudeSpan * squeeze * 111_320),
    };
}
/** The bounds that hold every point the board is drawing, or nothing if it is drawing none. */
export function boundsOf(google, points) {
    if (points.length === 0)
        return undefined;
    const bounds = new google.maps.LatLngBounds();
    for (const point of points)
        bounds.extend(new google.maps.LatLng(point.latitude, point.longitude));
    return bounds.isEmpty() ? undefined : bounds;
}
/**
 * The map's own options, chosen rather than defaulted.
 *
 * Every control Google offers is off. The design draws its own zoom and layer controls beside the
 * surface, and a second set of Google's in the corner would be two ways to do one thing and a
 * visible difference from the design in the one place a fidelity check looks.
 */
export const MAP_OPTIONS = {
    disableDefaultUI: true,
    clickableIcons: false,
    // Kenya, so a map with nothing on it yet opens where this platform operates rather than in the
    // Atlantic. Replaced by `fitBounds` the moment there is anything to show.
    center: { lat: -1.2921, lng: 36.8219 },
    zoom: 12,
    /*
     * A greyscale base, because the markers carry the colour. The design's surface is near-white
     * with pale roads and a green park, and every state on this board is told by a marker's hue —
     * a full-colour satellite or a vivid road map would compete with the one channel that means
     * something.
     */
    styles: [
        { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
        { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
        { elementType: "geometry", stylers: [{ saturation: -80 }] },
    ],
};
/**
 * Hear about an authentication failure, and stop Google drawing its own error over the map.
 *
 * `gm_authFailure` is the documented hook, and defining it does two things at once: it tells the
 * page that the key was refused, and it *replaces* Google's default behaviour, which is to paint
 * a grey panel reading "Oops! Something went wrong. This page didn't load Google Maps correctly."
 * across the map container.
 *
 * That panel is the wrong answer for this platform, and it is worth being precise about why. Every
 * surface here already has a correct render for "no tiles" — the design's own schematic, which is
 * what the design file draws and what the approved screenshots show. A refused key should fall
 * back to that drawing, not replace a working screen with an error about a billing console the
 * person looking at it cannot open. The dispatcher needs the driver positions either way.
 *
 * The failure arrives *after* `loadGoogleMaps` resolves: the key is checked when a map is
 * constructed and asks for a viewport, not when the script loads. So this is a separate
 * registration rather than a rejection, and the caller treats it as "the tiles went away".
 *
 * Returns the function that unregisters it.
 */
export function onMapsAuthFailure(handler) {
    const previous = globalThis["gm_authFailure"];
    globalThis["gm_authFailure"] = handler;
    return () => {
        /*
         * Restore rather than delete. Two maps on one page each register, and the second one leaving
         * should not take the first one's handler with it.
         */
        globalThis["gm_authFailure"] = previous;
    };
}
//# sourceMappingURL=index.js.map